import React from "react";
import DatePicker from "react-datepicker";
import dayjs from "dayjs";
import "react-datepicker/dist/react-datepicker.css";

// Formats we may receive an existing date value in (kept in sync with the
// formats used across the app when reading/writing dates).
const DATE_PARSE_FORMATS = ["YYYY-MM-DD", "DD-MM-YYYY", "DD/MM/YYYY", "YYYY/MM/DD"];

const parseDateValue = (value) => {
  if (!value) return null;
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }
  const strict = dayjs(value, DATE_PARSE_FORMATS, true);
  if (strict.isValid()) return strict.toDate();
  const loose = dayjs(value);
  return loose.isValid() ? loose.toDate() : null;
};

// As the user types raw digits (e.g. "26011991"), auto-insert the "-"
// separators so the text always matches dateFormat ("dd-MM-yyyy") and
// parses correctly, instead of requiring the user to type the dashes
// themselves.
const maskTypedDate = (raw) => {
  const digits = (raw || "").replace(/\D/g, "").slice(0, 8);
  const day = digits.slice(0, 2);
  const month = digits.slice(2, 4);
  const year = digits.slice(4, 8);
  return [day, month, year].filter(Boolean).join("-");
};

export const InputField = ({
  type,
  error,
  name,
  id,
  placeholder,
  register,
  value,
  disabled,
  maxLength,
  numeric,
  pattern,
  isFieldDisabled,
  onChange,
  children,
  checked,
  required,
}) => {
  const inputClassNames = `
    ${error ? "required form-check-inputt" : ""}
    ${isFieldDisabled ? "disabled-field" : ""}
  `.trim();

  if (type === "date") {
    // Native date inputs only let users pick from the calendar in a
    // consistent way across browsers; react-datepicker lets the user either
    // type the date directly or open the calendar to select it, while still
    // reporting the same "YYYY-MM-DD" string value the rest of the app
    // (validation, submit payloads, etc.) already expects.
    const fieldName = name || id;
    const fieldId = id || name;

    return (
      <div style={{ position: "relative" }} className="app-date-field">
        <DatePicker
          id={fieldId}
          name={fieldName}
          selected={parseDateValue(value)}
          onChange={(date) => {
            if (!onChange) return;
            const formattedValue = date ? dayjs(date).format("YYYY-MM-DD") : "";
            onChange({
              target: { name: fieldName, id: fieldId, value: formattedValue, type: "date" },
            });
          }}
          dateFormat="dd-MM-yyyy"
          // Always show the expected typing format (dd-mm-yyyy) rather than a
          // caller-supplied placeholder, so users always know how to type it.
          placeholderText="dd-mm-yyyy"
          className={inputClassNames}
          disabled={disabled}
          required={required}
          autoComplete="off"
          isClearable={!disabled}
          onChangeRaw={(e) => {
            if (e?.target && typeof e.target.value === "string") {
              e.target.value = maskTypedDate(e.target.value);
            }
          }}
          showIcon
          showMonthDropdown
          showYearDropdown
          dropdownMode="select"
          scrollableYearDropdown
          yearDropdownItemNumber={100}
          showPopperArrow={false}
          popperClassName="app-date-popper"
          calendarClassName="app-date-calendar"
        />
        {children && <div className="input-icon">{children}</div>}
        {error && <div className="error-message">{error}</div>}
      </div>
    );
  }

  return (
    <div style={{ position: "relative" }}>
      <input
        type={type}
        id={name}
        name={name}
        value={value}
        maxLength={maxLength}
        onChange={onChange}
        checked={checked}
        {...(register ? register(name) : {})}
        placeholder={placeholder}
        className={inputClassNames}
        disabled={disabled}
        numeric={numeric}
        pattern={pattern}
      />
      {children && <div className="input-icon">{children}</div>}
      {error && <div className="error-message">{error}</div>}
    </div>
  );
};
